import { useState, useEffect, useRef, useCallback } from 'react';
import * as chatbotService from '@/services/chatbot.service';
import { GENERAL_POLLING_INTERVAL } from '@/constants';

/**
 * Custom hook to manage knowledge base job status polling
 * @param {string} kbId - The knowledge base ID
 * @returns {Object} - Job status data and loading state
 */
export const useKnowledgeBaseJobStatus = (kbId) => {
  const [jobStatusData, setJobStatusData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);

  const fetchJobStatus = useCallback(async () => {
    if (!kbId) return;

    try {
      setError(null);
      const response = await chatbotService.getKnowledgeBaseJobStatus(kbId);
      setJobStatusData(response.data);
    } catch (error) {
      console.error('Error fetching knowledge base job status:', error);
      setError(error);
      if (error.response?.status === 404) {
        setJobStatusData(null);
      }
    }
  }, [kbId]);

  useEffect(() => {
    if (!kbId) return;

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Initial fetch
    setIsLoading(true);
    fetchJobStatus().finally(() => setIsLoading(false));

    // Set up polling interval (half as much as running jobs endpoint)
    // GENERAL_POLLING_INTERVAL is 4000ms, so we use 8000ms (double)
    const interval = setInterval(fetchJobStatus, GENERAL_POLLING_INTERVAL * 2);
    pollingIntervalRef.current = interval;

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [kbId, fetchJobStatus]);

  return {
    jobStatusData,
    isLoading,
    error,
    refetch: fetchJobStatus
  };
};

export default useKnowledgeBaseJobStatus;
